import React, { Fragment, PropsWithChildren, useState } from 'react';
import Link from 'next/link';

import ivclass from '@invideoio/web-shared/Modules/IVClass';
import { buildCategoryTree } from '@utils/template-category/template.utils';
import SlideDrawer from './SlideDrawer';

interface CategoryTemplateLeftPanelProps {
  templateCategories: any[];
  templateCategoryName?: string;
}

interface CollapseProps {
  buttonText?: string;
  buttonClass?: string;
  wrapperClass?: string;
}

const Collapse = (props: PropsWithChildren<CollapseProps>) => {
  const { buttonText, buttonClass, wrapperClass, children } = props;
  const [isCollapesed, setIsCollapesed] = useState(true);

  const toggleCollapse = () => {
    setIsCollapesed(currentState => !currentState);
  };

  return (
    <Fragment>
      <button
        className={ivclass('iv-flex iv-items-center', buttonClass)}
        type="button"
        onClick={toggleCollapse}
      >
        <svg
          width="10"
          height="10"
          viewBox="0 0 8 8"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className={ivclass(
            'iv-mr-5',
            !isCollapesed && 'iv-rotate-90',
            'iv-transition-all iv-duration-100 iv-ease-linear',
          )}
        >
          <path
            d="M7.39429 4.32902C7.64756 4.18275 7.64758 3.81716 7.39429 3.67088L1.56515 0.304552C1.31181 0.158245 0.995117 0.341089 0.995117 0.63362V7.36628C0.995117 7.65882 1.31182 7.84164 1.56515 7.69534L7.39429 4.32902Z"
            fill="black"
            fillOpacity="0.4"
          />
        </svg>
        {buttonText}
      </button>

      <div
        className={ivclass(
          isCollapesed ? 'iv-max-h-0' : 'iv-max-h-96',
          'iv-overflow-hidden',
          'iv-transition-all iv-duration-500 iv-ease-linear',
          wrapperClass,
        )}
      >
        {children}
      </div>
    </Fragment>
  );
};

const CategoryList = ({ data, containerStyles }: any) => {
  return (
    <ul className={ivclass('iv-list-none', containerStyles)}>
      <li
        className={ivclass(
          'iv-list-none iv-py-5 iv-px-15',
          'iv-text-body-m iv-font-semibold iv-text-grey-85',
        )}
      >
        <Link href={`/templates/`}>All Categories</Link>
      </li>
      {Object.keys(data).map(category => {
        if (data[category].length > 1) {
          return (
            <div key={category} className={ivclass('iv-py-5')}>
              <Collapse
                buttonText={category}
                buttonClass={ivclass(
                  'iv-text-body-m iv-font-medium iv-text-grey-60 hover:iv-text-grey-80',
                )}
              >
                {data[category].map((cat: any, index: number) => (
                  <li
                    key={cat.CategoryName}
                    className={ivclass(
                      'iv-list-none iv-py-5 iv-pl-25',
                      'iv-text-body-m iv-font-medium iv-text-grey-60 hover:iv-text-grey-80',
                      index === 0 && 'iv-mt-5',
                    )}
                  >
                    <Link href={`/templates/${cat.Slug}/`}>
                      {cat.CategoryName}
                    </Link>
                  </li>
                ))}
              </Collapse>
            </div>
          );
        }

        return (
          <li
            key={data[category][0].Slug}
            className={ivclass(
              'iv-list-none iv-py-5 iv-px-15',
              'iv-text-body-m iv-font-medium iv-text-grey-60 hover:iv-text-grey-80',
            )}
          >
            <Link href={`/templates/${data[category][0].Slug}/`}>
              {data[category][0].CategoryName}
            </Link>
          </li>
        );
      })}
    </ul>
  );
};

function CategoryTemplateLeftPanel(props: CategoryTemplateLeftPanelProps) {
  const { templateCategories, templateCategoryName } = props;

  const [isOpen, setIsOpen] = useState(false);

  const data = buildCategoryTree(templateCategories);

  const toggleDrawer = () => {
    setIsOpen(current => !current);
  };

  return (
    <div
      className={ivclass(
        'iv-w-[220px] sm:iv-w-full md:iv-w-full',
        'iv-py-25 iv-pr-15 sm:iv-pb-5 sm:iv-pt-15 md:iv-pt-15 md:iv-pb-5 md:iv-pr-5 sm:iv-pr-5',
        'iv-border-r iv-border-grey-5 md:iv-border-0 sm:iv-border-0',
      )}
    >
      <div
        className={ivclass(
          'iv-w-full',
          'iv-flex iv-justify-between iv-items-center',
          'iv-hidden md:iv-flex sm:iv-flex',
        )}
      >
        <div
          className={ivclass(
            'iv-text-body-l iv-font-semibold iv-inline-block',
            'iv-pl-25 md:iv-pl-5 sm:iv-pl-5',
          )}
        >
          {templateCategoryName}
        </div>
        <button
          type="button"
          onClick={toggleDrawer}
          className={ivclass(
            'iv-flex iv-items-center',
            'iv-text-body-m iv-m-body-m iv-px-10 iv-py-5 iv-rounded',
            'hover:iv-bg-grey-5',
          )}
        >
          <span className="iv-mr-10">Categories</span>
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M1 3C1 2.72386 1.22386 2.5 1.5 2.5H14.5C14.7761 2.5 15 2.72386 15 3C15 3.27614 14.7761 3.5 14.5 3.5H1.5C1.22386 3.5 1 3.27614 1 3ZM3 8C3 7.72386 3.22386 7.5 3.5 7.5H12.5C12.7761 7.5 13 7.72386 13 8C13 8.27614 12.7761 8.5 12.5 8.5H3.5C3.22386 8.5 3 8.27614 3 8ZM6 13C6 12.7239 6.22386 12.5 6.5 12.5H9.5C9.77614 12.5 10 12.7239 10 13C10 13.2761 9.77614 13.5 9.5 13.5H6.5C6.22386 13.5 6 13.2761 6 13Z"
              fill="#262626"
            />
          </svg>
        </button>
        <SlideDrawer
          isOpen={isOpen}
          onClose={toggleDrawer}
          drawerHead={() => (
            <span className={ivclass('iv-font-semibold iv-text-body-l')}>
              Categories
            </span>
          )}
          drawerStyles={{
            width: '320px',
            padding: '0 0px',
          }}
        >
          <CategoryList data={data} containerStyles={`iv-px-10`} />
        </SlideDrawer>
      </div>
      <div className={ivclass('md:iv-hidden sm:iv-hidden', 'iv-pl-9')}>
        <CategoryList data={data} />
      </div>
    </div>
  );
}

export default CategoryTemplateLeftPanel;
